import React, { useEffect, useRef } from 'react';
import { Alert, Button, Collapse, Empty, Space, Table, Upload } from 'antd';
import {
  FileExcelOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useOrderImport } from '../contexts/OrderImportContext';

const ExcelUpload = ({ onViewOrders, onFindOrder }) => {
  const { file, uploading, error, result, chooseFile, submit } =
    useOrderImport();
  const headingRef = useRef(null);
  useEffect(() => {
    if (result) headingRef.current?.focus();
  }, [result]);
  const firstSheet = result?.sheetNames?.[0];
  const sheetRows = result?.data?.[firstSheet];
  const ordersToCheck = (Array.isArray(sheetRows) ? sheetRows.slice(1) : [])
    .map((row, index) => ({
      row: index + 1,
      storeName: String(row?.[0] || ''),
      orderNo: String(row?.[1] || '').trim(),
    }))
    .filter((row) => row.orderNo);
  return (
    <div className="upload-workspace">
      <div className="table-toolbar">
        <div>
          <h2 className="upload-heading" ref={headingRef} tabIndex={-1}>
            {result ? '核对导入结果' : '上传订单表'}
          </h2>
          {!result && (
            <span className="muted">使用现有模板，减少字段格式问题。</span>
          )}
        </div>
        {result ? (
          <Button
            onClick={() => {
              chooseFile(null);
              headingRef.current?.focus({ preventScroll: true });
            }}
          >
            另选文件
          </Button>
        ) : (
          <Button
            icon={<DownloadOutlined />}
            href="/amazon_order_template.xlsx"
            download="amazon_order_template.xlsx"
          >
            下载模板
          </Button>
        )}
      </div>
      {!result && (
        <>
          <Upload.Dragger
            accept=".xls,.xlsx"
            multiple={false}
            beforeUpload={chooseFile}
            disabled={uploading}
            showUploadList={false}
          >
            <FileExcelOutlined
              style={{ fontSize: 32, color: 'var(--gold-ink)', marginBottom: 16 }}
            />
            <p className="ant-upload-text">拖入文件，或点击选择</p>
            <p className="ant-upload-hint">
              Excel（.xls / .xlsx）· 小于 10 MB · 选择后需确认提交
            </p>
          </Upload.Dragger>
          {file && (
            <div className="upload-file">
              <span>
                <strong>{file.name}</strong>{' '}
                <span className="muted">{(file.size / 1024).toFixed(1)} KB</span>
              </span>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                disabled={uploading}
                onClick={() => chooseFile(null)}
              >
                移除
              </Button>
            </div>
          )}
          {error && (
            <Alert className="inline-error" type="error" showIcon message={error} />
          )}
          {uploading && (
            <Alert
              className="inline-error"
              type="info"
              showIcon
              message="正在上传并处理文件"
              description="可以切换工作区页面，返回导入页查看结果。处理完成前请勿刷新或关闭浏览器。"
            />
          )}
          <Space wrap style={{ margin: '16px 0 24px' }}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={submit}
              disabled={!file}
              loading={uploading}
            >
              {error && file ? '重新提交文件' : '提交文件'}
            </Button>
            {onViewOrders && (
              <Button onClick={onViewOrders} disabled={uploading}>
                查看待审核订单
              </Button>
            )}
          </Space>
        </>
      )}
      {result && (
        <section className="upload-result" aria-live="polite">
          <p className="muted upload-result-file">
            {result.fileName} · 返回于{' '}
            {new Date(result.returnedAt).toLocaleString('zh-CN', {
              hour12: false,
            })}
          </p>
          <Alert
            type="info"
            showIcon
            message="已收到文件结果，订单入库情况待核对"
            description="文件解析结果不代表订单已经入库。查询下方订单，核对内容后再继续处理。"
          />
          {ordersToCheck.length > 0 && (
            <div className="import-checklist">
              <h3 className="detail-section-title">
                待核对订单 · {ordersToCheck.length} 笔
              </h3>
              <p className="muted">
                来自首个工作表「{firstSheet}」。只有首个工作表用于导入。
              </p>
              <Table
                rowKey="row"
                size="small"
                dataSource={ordersToCheck}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  hideOnSinglePage: true,
                }}
                columns={[
                  { title: '序号', dataIndex: 'row', width: 64 },
                  {
                    title: '订单 / 店铺',
                    key: 'order',
                    render: (_, row) => (
                      <div>
                        <span className="order-number">{row.orderNo}</span>
                        <div className="muted">
                          {row.storeName || '未填写店铺'}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: '核对',
                    key: 'check',
                    width: 72,
                    render: (_, row) => (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onFindOrder?.(row.orderNo)}
                      >
                        查询
                      </Button>
                    ),
                  },
                ]}
              />
            </div>
          )}
          {!ordersToCheck.length && (
            <p className="import-checklist muted">
              返回文件中没有可核对的订单编号，可展开文件内容检查，或返回订单列表查询。
            </p>
          )}
          <Collapse
            ghost
            items={[
              {
                key: 'parsed',
                label: '查看返回文件内容',
                children: (
                  <>
                    {(result.sheetNames || []).map((name) => {
                      const rows = Array.isArray(result.data?.[name])
                        ? result.data[name]
                        : [];
                      return (
                        <section key={name} style={{ marginTop: 24 }}>
                          <h3 style={{ fontSize: 14 }}>
                            {name}{' '}
                            <span className="muted">· 最多预览前 5 行</span>
                          </h3>
                          {rows.length ? (
                            <div className="upload-preview">
                              <table>
                                <thead>
                                  <tr>
                                    {(rows[0] || []).map((cell, index) => (
                                      <th key={index}>{String(cell ?? '')}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.slice(1, 6).map((row, index) => (
                                    <tr key={index}>
                                      {row.map((cell, column) => (
                                        <td key={column}>
                                          {String(cell ?? '')}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="此工作表没有可预览内容"
                            />
                          )}
                        </section>
                      );
                    })}
                  </>
                ),
              },
            ]}
          />
        </section>
      )}
      <Collapse
        ghost
        items={[
          {
            key: 'format',
            label: '模板字段与填写说明',
            children: (
              <div className="muted">
                <p>
                  第一行为标题行。B 列订单号、K 列客户 ID 为必填项。商品
                  ASIN、数量与预期价格按顺序对应，多个值用逗号分隔。
                </p>
                <p>
                  A–E：店铺、平台订单号、收货人、地址、城市
                  <br />
                  F–J：州 / 省、邮编、电话、亚马逊订单号、客户账号 ID
                  <br />
                  K–O：客户 ID、国家、商品 ASIN、数量、预期价格
                </p>
                <p>
                  提交后将开始处理文件，无需再次确认。收到结果后可查询订单，核对入库情况。
                </p>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
export default ExcelUpload;
